import { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

interface DroneProperties {
  drone_id: string;
  height: number;
  horizontal_speed: number;
  vertical_speed: number;
  battery_capacity: number;
  battery_temperature1: number | null;
  battery_temperature2: number | null;
  remaining_flight_time: number;
  gimbal_pitch: number | null;
  gimbal_roll: number | null;
  gimbal_yaw: number | null;
  zoom_factor: number;
  gps_satellites: number;
  home_distance: number;
  wind_speed: number;
  wind_direction: number;
  temperature: number | null;
  heading: number;
  camera_type: string;
  last_update: string;
  measure_target_distance: number | null;
  measure_target_altitude: number | null;
  measure_target_geom: string | null;
}

interface DroneFeature {
  type: string;
  id: string;
  geometry: {
    type: string;
    coordinates: [number, number];
  };
  properties: DroneProperties;
}

interface DroneLayerProps {
  wfsUrl: string;
  refreshInterval?: number; // in milliseconds
  visible?: boolean;
}

// Helper function to create drone icon
const createDroneIcon = (drone: DroneProperties) => {
  const batteryColor =
    drone.battery_capacity > 50 ? '#10b981' : // green
    drone.battery_capacity > 20 ? '#f59e0b' : // orange
    '#ef4444'; // red

  const svgIcon = `
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style="transform: rotate(${drone.heading}deg); transform-origin: center;">
      <!-- Drone body -->
      <circle cx="20" cy="20" r="6" fill="#1f2937" stroke="#fff" stroke-width="1.5"/>

      <!-- Propeller arms -->
      <line x1="10" y1="10" x2="20" y2="20" stroke="#374151" stroke-width="2"/>
      <line x1="30" y1="10" x2="20" y2="20" stroke="#374151" stroke-width="2"/>
      <line x1="10" y1="30" x2="20" y2="20" stroke="#374151" stroke-width="2"/>
      <line x1="30" y1="30" x2="20" y2="20" stroke="#374151" stroke-width="2"/>

      <!-- Propellers -->
      <circle cx="10" cy="10" r="4" fill="#3b82f6" opacity="0.7"/>
      <circle cx="30" cy="10" r="4" fill="#3b82f6" opacity="0.7"/>
      <circle cx="10" cy="30" r="4" fill="#3b82f6" opacity="0.7"/>
      <circle cx="30" cy="30" r="4" fill="#3b82f6" opacity="0.7"/>

      <!-- Center indicator -->
      <circle cx="20" cy="20" r="3" fill="${batteryColor}"/>

      <!-- Heading indicator (pointing forward) -->
      <line x1="20" y1="20" x2="20" y2="12" stroke="#ef4444" stroke-width="2" stroke-linecap="round"/>
    </svg>
  `;

  const droneLabel = `
    <div class="drone-label">
      <div style="font-weight: 600; color: #1f2937;">${drone.drone_id.substring(0, 8)}...</div>
      <div style="font-size: 10px; color: #6b7280;">🔋 ${drone.battery_capacity}% | ↑ ${Math.round(drone.height)}m</div>
    </div>
  `;

  return L.divIcon({
    html: `${droneLabel}${svgIcon}`,
    className: 'drone-icon',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  });
};

const DroneLayer = ({ wfsUrl, refreshInterval = 5000, visible = true }: DroneLayerProps) => {
  const map = useMap();
  const [drones, setDrones] = useState<DroneFeature[]>([]);
  const [markers, setMarkers] = useState<L.Marker[]>([]);
  const [lrfMarkers, setLrfMarkers] = useState<L.CircleMarker[]>([]);
  const [lrfLines, setLrfLines] = useState<L.Polyline[]>([]);

  // Fetch drone data from WFS
  const fetchDrones = async () => {
    if (!visible) return;

    try {
      const response = await fetch(wfsUrl);
      const data = await response.json();

      if (data.features && Array.isArray(data.features)) {
        // Filter out drones that haven't updated in the last 60 seconds
        const now = new Date().getTime();
        const onlineThresholdMs = 60 * 1000; // 60 seconds

        const onlineDrones = data.features.filter((drone: DroneFeature) => {
          try {
            const lastUpdate = new Date(drone.properties.last_update).getTime();
            const timeSinceUpdate = now - lastUpdate;
            return timeSinceUpdate <= onlineThresholdMs;
          } catch (e) {
            console.error('Error parsing drone last_update:', e);
            return false; // Exclude drones with invalid timestamps
          }
        });

        console.log(`Drones: ${onlineDrones.length} online / ${data.features.length} total`);
        setDrones(onlineDrones);
      }
    } catch (error) {
      console.error('Error fetching drone data:', error);
    }
  };

  // Initial fetch and set up polling
  useEffect(() => {
    if (!visible) {
      // Clear markers when not visible
      markers.forEach(marker => map.removeLayer(marker));
      lrfMarkers.forEach(marker => map.removeLayer(marker));
      lrfLines.forEach(line => map.removeLayer(line));
      setMarkers([]);
      setLrfMarkers([]);
      setLrfLines([]);
      return;
    }

    fetchDrones();
    const interval = setInterval(fetchDrones, refreshInterval);

    return () => {
      clearInterval(interval);
    };
  }, [visible, refreshInterval]);

  // Update markers when drone data changes
  useEffect(() => {
    // Clear existing markers and LRF elements
    markers.forEach(marker => map.removeLayer(marker));
    lrfMarkers.forEach(marker => map.removeLayer(marker));
    lrfLines.forEach(line => map.removeLayer(line));

    if (!visible) {
      setMarkers([]);
      setLrfMarkers([]);
      setLrfLines([]);
      return;
    }

    // Create new markers and LRF elements
    const newMarkers: L.Marker[] = [];
    const newLrfMarkers: L.CircleMarker[] = [];
    const newLrfLines: L.Polyline[] = [];

    drones.forEach((drone) => {
      const [lng, lat] = drone.geometry.coordinates;
      const props = drone.properties;

      const marker = L.marker([lat, lng], {
        icon: createDroneIcon(props),
      });

      // Create detailed popup
      const lastUpdate = new Date(props.last_update).toLocaleString();
      const popupContent = `
        <div style="min-width: 280px; font-family: system-ui;">
          <div style="margin-bottom: 12px; padding-bottom: 12px; border-bottom: 2px solid #e5e7eb;">
            <div style="font-size: 16px; font-weight: 600; color: #1f2937; margin-bottom: 4px;">
              🚁 ${props.drone_id}
            </div>
            <div style="font-size: 11px; color: #6b7280;">
              Last Update: ${lastUpdate}
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px;">
            <div style="background: #f3f4f6; padding: 8px; border-radius: 6px;">
              <div style="font-size: 10px; color: #6b7280; font-weight: 500;">ALTITUDE</div>
              <div style="font-size: 16px; font-weight: 600; color: #1f2937;">${Math.round(props.height)}m</div>
            </div>
            <div style="background: #f3f4f6; padding: 8px; border-radius: 6px;">
              <div style="font-size: 10px; color: #6b7280; font-weight: 500;">BATTERY</div>
              <div style="font-size: 16px; font-weight: 600; color: ${props.battery_capacity > 50 ? '#10b981' : props.battery_capacity > 20 ? '#f59e0b' : '#ef4444'};">
                ${props.battery_capacity}%
              </div>
            </div>
            <div style="background: #f3f4f6; padding: 8px; border-radius: 6px;">
              <div style="font-size: 10px; color: #6b7280; font-weight: 500;">H. SPEED</div>
              <div style="font-size: 16px; font-weight: 600; color: #1f2937;">${props.horizontal_speed.toFixed(1)} m/s</div>
            </div>
            <div style="background: #f3f4f6; padding: 8px; border-radius: 6px;">
              <div style="font-size: 10px; color: #6b7280; font-weight: 500;">V. SPEED</div>
              <div style="font-size: 16px; font-weight: 600; color: #1f2937;">${props.vertical_speed.toFixed(1)} m/s</div>
            </div>
          </div>

          <div style="background: #f9fafb; padding: 10px; border-radius: 6px; font-size: 12px;">
            <div style="margin-bottom: 4px;"><strong>Heading:</strong> ${props.heading.toFixed(1)}°</div>
            <div style="margin-bottom: 4px;"><strong>Home Distance:</strong> ${props.home_distance.toFixed(1)}m</div>
            <div style="margin-bottom: 4px;"><strong>GPS Satellites:</strong> ${props.gps_satellites}</div>
            <div style="margin-bottom: 4px;"><strong>Camera:</strong> ${props.camera_type}</div>
            ${props.gimbal_pitch !== null && props.gimbal_roll !== null && props.gimbal_yaw !== null ? `<div style="margin-bottom: 4px;"><strong>Gimbal:</strong> P:${props.gimbal_pitch.toFixed(1)}° R:${props.gimbal_roll.toFixed(1)}° Y:${props.gimbal_yaw.toFixed(1)}°</div>` : ''}
            ${props.battery_temperature1 !== null && props.battery_temperature2 !== null ? `<div style="margin-bottom: 4px;"><strong>Battery Temp:</strong> ${props.battery_temperature1.toFixed(1)}°C / ${props.battery_temperature2.toFixed(1)}°C</div>` : ''}
            ${props.wind_speed > 0 ? `<div style="margin-bottom: 4px;"><strong>Wind:</strong> ${props.wind_speed.toFixed(1)} m/s @ ${props.wind_direction}°</div>` : ''}
            ${props.measure_target_distance !== null ? `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;"><strong>🎯 LRF Target:</strong> ${props.measure_target_distance.toFixed(1)}m${props.measure_target_altitude !== null ? ` @ ${props.measure_target_altitude.toFixed(1)}m alt` : ''}</div>` : ''}
          </div>

          <div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; text-align: center;">
            Position: ${lat.toFixed(6)}, ${lng.toFixed(6)}
          </div>
        </div>
      `;

      marker.bindPopup(popupContent);
      marker.addTo(map);
      newMarkers.push(marker);

      // Add LRF target if available
      if (props.measure_target_geom && props.measure_target_distance !== null) {
        try {
          // Parse the GeoJSON Point format: {"type":"Point","coordinates":[lng, lat]}
          let targetGeom;
          if (typeof props.measure_target_geom === 'string') {
            targetGeom = JSON.parse(props.measure_target_geom);
          } else {
            targetGeom = props.measure_target_geom;
          }

          if (targetGeom && targetGeom.coordinates && Array.isArray(targetGeom.coordinates)) {
            const targetLng = targetGeom.coordinates[0];
            const targetLat = targetGeom.coordinates[1];

            if (!isNaN(targetLat) && !isNaN(targetLng)) {
              // Create line from drone to target
              const line = L.polyline(
                [[lat, lng], [targetLat, targetLng]],
                {
                  color: '#ef4444',
                  weight: 2,
                  opacity: 0.6,
                  dashArray: '5, 5',
                }
              );
              line.addTo(map);
              newLrfLines.push(line);

              // Create LRF target marker (red dot)
              const lrfMarker = L.circleMarker([targetLat, targetLng], {
                radius: 6,
                fillColor: '#ef4444',
                color: '#fff',
                weight: 2,
                opacity: 1,
                fillOpacity: 0.9,
              });

              const lrfPopupContent = `
                <div style="min-width: 200px;">
                  <div style="font-weight: 600; margin-bottom: 8px;">🎯 LRF Target</div>
                  <div style="font-size: 12px; margin-bottom: 4px;"><strong>Drone:</strong> ${props.drone_id.substring(0, 12)}...</div>
                  <div style="font-size: 12px; margin-bottom: 4px;"><strong>Distance:</strong> ${props.measure_target_distance.toFixed(1)}m</div>
                  ${props.measure_target_altitude !== null ? `<div style="font-size: 12px; margin-bottom: 4px;"><strong>Altitude:</strong> ${props.measure_target_altitude.toFixed(1)}m</div>` : ''}
                  <div style="font-size: 11px; color: #666; margin-top: 8px;">Position: ${targetLat.toFixed(6)}, ${targetLng.toFixed(6)}</div>
                </div>
              `;

              lrfMarker.bindPopup(lrfPopupContent);
              lrfMarker.addTo(map);
              newLrfMarkers.push(lrfMarker);
            }
          }
        } catch (error) {
          console.error('Error parsing LRF target geometry:', error);
        }
      }
    });

    setMarkers(newMarkers);
    setLrfMarkers(newLrfMarkers);
    setLrfLines(newLrfLines);

    return () => {
      newMarkers.forEach(marker => map.removeLayer(marker));
      newLrfMarkers.forEach(marker => map.removeLayer(marker));
      newLrfLines.forEach(line => map.removeLayer(line));
    };
  }, [drones, map, visible]);

  return null;
};

export default DroneLayer;
